import React, { useState } from 'react';
import { Row, Col, Card, Table, Badge, Form, Button } from 'react-bootstrap';
import { 
  BarChart, Bar, PieChart, Pie, LineChart, Line, XAxis, YAxis, 
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell, AreaChart, Area
} from 'recharts';
import { FaFileDownload, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';

// Utils
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '$0.00';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value);
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return '0%';
  return `${(value * 100).toFixed(1)}%`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getStatusBadgeVariant = (status) => {
  switch (status) {
    case 'Paid':
      return 'success';
    case 'Partial':
      return 'info';
    case 'Overdue':
      return 'danger';
    case 'Pending':
      return 'warning';
    default:
      return 'secondary';
  }
};

// Color palette for charts
const COLORS = [
  '#3366CC', '#DC3912', '#FF9900', '#109618', '#990099', 
  '#0099C6', '#DD4477', '#66AA00', '#B82E2E', '#316395'
];

const FinancialReport = ({ data, viewMode = 'both' }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [searchTerm, setSearchTerm] = useState('');
  
  if (!data || !data.data) {
    return <div>No data available</div>;
  }
  
  const reportType = data.type;
  let reportData = data.data;
  
  // Calculate summary statistics
  const calculateSummary = () => {
    if (reportType === 'revenue-summary') {
      const totalRevenue = reportData.summary?.totalRevenue || 0;
      const outstandingAmount = reportData.summary?.outstandingAmount || 0;
      
      return {
        totalRevenue,
        outstandingAmount,
        collectionRate: totalRevenue > 0 ? (totalRevenue - outstandingAmount) / totalRevenue : 0,
        projectCount: reportData.details?.length || 0
      };
    } else if (reportType === 'project-profitability') {
      const totalRevenue = reportData.projects?.reduce((sum, p) => sum + (p.Revenue || 0), 0) || 0;
      const totalCost = reportData.projects?.reduce((sum, p) => sum + (p.TotalCost || 0), 0) || 0;
      const totalProfit = totalRevenue - totalCost;
      
      return {
        totalRevenue,
        totalCost,
        totalProfit,
        averageMargin: totalRevenue > 0 ? totalProfit / totalRevenue : 0,
        projectCount: reportData.projects?.length || 0
      };
    } else if (reportType === 'payment-schedules') {
      const totalScheduled = reportData.schedules?.reduce((sum, s) => sum + (s.TotalAmount || 0), 0) || 0;
      const totalReceived = reportData.schedules?.reduce((sum, s) => sum + (s.AmountReceived || 0), 0) || 0;
      
      return {
        totalScheduled,
        totalReceived,
        paymentCompletion: totalScheduled > 0 ? totalReceived / totalScheduled : 0,
        scheduleCount: reportData.schedules?.length || 0
      };
    } else if (reportType === 'outstanding-payments') {
      const totalOutstanding = reportData.payments?.reduce((sum, p) => sum + (p.AmountDue || 0), 0) || 0;
      const overdueAmount = reportData.payments?.reduce((sum, p) => {
        if (p.Status === 'Overdue') {
          return sum + (p.AmountDue || 0);
        }
        return sum;
      }, 0) || 0;
      
      return {
        totalOutstanding,
        overdueAmount,
        overduePercentage: totalOutstanding > 0 ? overdueAmount / totalOutstanding : 0,
        paymentCount: reportData.payments?.length || 0
      };
    }
    
    return {};
  };
  
  const summary = calculateSummary();
  
  // Sort table data
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };
  
  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) {
      return <FaSort className="ms-1 text-muted" />;
    }
    return sortConfig.direction === 'ascending' ? 
      <FaSortUp className="ms-1" /> : <FaSortDown className="ms-1" />;
  };
  
  const getSortedData = (items) => {
    if (!items || !sortConfig.key) return items;
    
    const sortableItems = [...items];
    sortableItems.sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];
      
      // Handle null values
      if (aValue === null) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (bValue === null) return sortConfig.direction === 'ascending' ? 1 : -1;
      
      // For strings, ignore case
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      // Dates need special handling
      if (sortConfig.key === 'Date' || sortConfig.key === 'DueDate' || sortConfig.key === 'PaymentDate') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }
      
      if (aValue < bValue) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
    
    return sortableItems;
  };
  
  // Filter table data
  const getFilteredData = (items) => {
    if (!items || !searchTerm) return items;
    
    return items.filter(item => {
      // Search through all string and number fields
      return Object.keys(item).some(key => {
        const value = item[key];
        if (value === null || value === undefined) return false;
        
        if (typeof value === 'string') {
          return value.toLowerCase().includes(searchTerm.toLowerCase());
        } else if (typeof value === 'number') {
          return value.toString().includes(searchTerm);
        }
        
        return false;
      });
    });
  };
  
  // Render revenue summary report
  const renderRevenueSummary = () => {
    // Group by month for time series
    const revenueByMonth = reportData.timeSeriesData || [];
    
    // Filter and sort revenue details
    let revenueDetails = reportData.details || [];
    if (searchTerm) {
      revenueDetails = getFilteredData(revenueDetails);
    }
    if (sortConfig.key) {
      revenueDetails = getSortedData(revenueDetails);
    }
    
    const renderCharts = () => (
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Revenue Over Time</h5>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={revenueByMonth}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis 
                    tickFormatter={(value) => `$${Math.floor(value / 1000)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Revenue']}
                  />
                  <Legend />
                  <Area 
                    type="monotone" 
                    dataKey="revenue" 
                    name="Revenue" 
                    fill="#82ca9d" 
                    stroke="#82ca9d"
                    fillOpacity={0.8}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Revenue by Client</h5>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={reportData.byClient || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                    paddingAngle={3}
                    dataKey="value"
                    nameKey="name"
                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {(reportData.byClient || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [formatCurrency(value), 'Revenue']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
    
    const renderRevenueTable = () => (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Revenue Details</h5>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ width: '200px' }}
              />
              <Button 
                variant="outline-secondary" 
                size="sm"
                title="Export as CSV"
              >
                <FaFileDownload />
              </Button>
            </div>
          </div>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th onClick={() => requestSort('ProjectName')} className="sortable">
                  Project {getSortIcon('ProjectName')}
                </th>
                <th onClick={() => requestSort('ClientName')} className="sortable">
                  Client {getSortIcon('ClientName')}
                </th>
                <th onClick={() => requestSort('Date')} className="sortable">
                  Date {getSortIcon('Date')}
                </th>
                <th onClick={() => requestSort('Revenue')} className="sortable">
                  Revenue {getSortIcon('Revenue')}
                </th>
                <th onClick={() => requestSort('Outstanding')} className="sortable">
                  Outstanding {getSortIcon('Outstanding')}
                </th>
                <th onClick={() => requestSort('Status')} className="sortable">
                  Status {getSortIcon('Status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {revenueDetails.map((item, index) => (
                <tr key={index}>
                  <td>{item.ProjectName}</td>
                  <td>{item.ClientName}</td>
                  <td>{formatDate(item.Date)}</td>
                  <td>{formatCurrency(item.Revenue || 0)}</td>
                  <td>{formatCurrency(item.Outstanding || 0)}</td>
                  <td>
                    <Badge bg={getStatusBadgeVariant(item.Status)}>
                      {item.Status}
                    </Badge>
                  </td>
                </tr>
              ))}
              {revenueDetails.length === 0 && (
                <tr>
                  <td colSpan="6" className="text-center">No revenue data found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
    
    return (
      <>
        <div className="summary-cards">
          <div className="summary-card">
            <h5>Total Revenue</h5>
            <h3>{formatCurrency(summary.totalRevenue)}</h3>
          </div>
          <div className="summary-card">
            <h5>Outstanding Amount</h5>
            <h3>{formatCurrency(summary.outstandingAmount)}</h3>
          </div>
          <div className="summary-card">
            <h5>Collection Rate</h5>
            <h3>{formatPercent(summary.collectionRate)}</h3>
          </div>
          <div className="summary-card">
            <h5>Project Count</h5>
            <h3>{summary.projectCount}</h3>
          </div>
        </div>
        
        {(viewMode === 'chart' || viewMode === 'both') && renderCharts()}
        {(viewMode === 'table' || viewMode === 'both') && renderRevenueTable()}
      </>
    );
  };
  
  // Render project profitability report
  const renderProjectProfitability = () => {
    // Filter and sort projects
    let projects = reportData.projects || [];
    if (searchTerm) {
      projects = getFilteredData(projects);
    }
    if (sortConfig.key) {
      projects = getSortedData(projects);
    }
    
    // Create data for most profitable projects chart (Top 10)
    const profitabilityData = [...projects]
      .sort((a, b) => {
        const profitA = (a.Revenue || 0) - (a.TotalCost || 0);
        const profitB = (b.Revenue || 0) - (b.TotalCost || 0);
        return profitB - profitA;
      })
      .slice(0, 10)
      .map(project => ({
        name: project.ProjectName.length > 15 ? project.ProjectName.substring(0, 15) + '...' : project.ProjectName,
        revenue: project.Revenue || 0,
        cost: project.TotalCost || 0,
        profit: (project.Revenue || 0) - (project.TotalCost || 0)
      }));
    
    // Create data for margin by project type
    const marginByType = {};
    projects.forEach(project => {
      if (project.ProjectType && project.Revenue && project.TotalCost) {
        if (!marginByType[project.ProjectType]) {
          marginByType[project.ProjectType] = {
            totalRevenue: 0,
            totalCost: 0,
            count: 0
          };
        }
        
        marginByType[project.ProjectType].totalRevenue += project.Revenue;
        marginByType[project.ProjectType].totalCost += project.TotalCost;
        marginByType[project.ProjectType].count += 1;
      }
    });
    
    const marginChartData = Object.entries(marginByType).map(([type, data], index) => {
      const profit = data.totalRevenue - data.totalCost;
      const margin = data.totalRevenue > 0 ? profit / data.totalRevenue : 0;
      
      return {
        name: type,
        value: margin,
        count: data.count,
        color: COLORS[index % COLORS.length]
      };
    });
    
    const renderCharts = () => (
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Most Profitable Projects (Top 10)</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={profitabilityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis 
                    tickFormatter={(value) => `$${Math.floor(value / 1000)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), '']}
                  />
                  <Legend />
                  <Bar dataKey="revenue" name="Revenue" fill="#82ca9d" />
                  <Bar dataKey="cost" name="Cost" fill="#ff8042" />
                  <Bar dataKey="profit" name="Profit" fill="#8884d8" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Profit Margin by Project Type</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={marginChartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis 
                    tickFormatter={(value) => `${(value * 100).toFixed(0)}%`}
                    domain={[0, Math.max(...marginChartData.map(item => item.value), 0.5)]}
                  />
                  <Tooltip 
                    formatter={(value) => [`${(value * 100).toFixed(1)}%`, 'Profit Margin']}
                  />
                  <Legend />
                  <Bar dataKey="value" name="Profit Margin" fill="#8884d8">
                    {marginChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
    
    const renderProfitabilityTable = () => (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Project Profitability Details</h5>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search projects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ width: '200px' }}
              />
              <Button 
                variant="outline-secondary" 
                size="sm"
                title="Export as CSV"
              >
                <FaFileDownload />
              </Button>
            </div>
          </div>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th onClick={() => requestSort('ProjectName')} className="sortable">
                  Project Name {getSortIcon('ProjectName')}
                </th>
                <th onClick={() => requestSort('ClientName')} className="sortable">
                  Client {getSortIcon('ClientName')}
                </th>
                <th onClick={() => requestSort('ProjectType')} className="sortable">
                  Type {getSortIcon('ProjectType')}
                </th>
                <th onClick={() => requestSort('Revenue')} className="sortable">
                  Revenue {getSortIcon('Revenue')}
                </th>
                <th onClick={() => requestSort('TotalCost')} className="sortable">
                  Cost {getSortIcon('TotalCost')}
                </th>
                <th onClick={() => requestSort('Profit')} className="sortable">
                  Profit {getSortIcon('Profit')}
                </th>
                <th onClick={() => requestSort('Margin')} className="sortable">
                  Margin {getSortIcon('Margin')}
                </th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project, index) => {
                const profit = (project.Revenue || 0) - (project.TotalCost || 0);
                const margin = project.Revenue > 0 ? profit / project.Revenue : 0;
                
                return (
                  <tr key={index}>
                    <td>{project.ProjectName}</td>
                    <td>{project.ClientName}</td>
                    <td>{project.ProjectType || 'N/A'}</td>
                    <td>{formatCurrency(project.Revenue || 0)}</td>
                    <td>{formatCurrency(project.TotalCost || 0)}</td>
                    <td className={profit >= 0 ? 'text-success' : 'text-danger'}>
                      {formatCurrency(profit)}
                    </td>
                    <td className={margin >= 0 ? 'text-success' : 'text-danger'}>
                      {formatPercent(margin)}
                    </td>
                  </tr>
                );
              })}
              {projects.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center">No project data found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
    
    return (
      <>
        <div className="summary-cards">
          <div className="summary-card">
            <h5>Total Revenue</h5>
            <h3>{formatCurrency(summary.totalRevenue)}</h3>
          </div>
          <div className="summary-card">
            <h5>Total Cost</h5>
            <h3>{formatCurrency(summary.totalCost)}</h3>
          </div>
          <div className="summary-card">
            <h5>Total Profit</h5>
            <h3 className={summary.totalProfit >= 0 ? 'text-success' : 'text-danger'}>
              {formatCurrency(summary.totalProfit)}
            </h3>
          </div>
          <div className="summary-card">
            <h5>Average Margin</h5>
            <h3 className={summary.averageMargin >= 0 ? 'text-success' : 'text-danger'}>
              {formatPercent(summary.averageMargin)}
            </h3>
          </div>
        </div>
        
        {(viewMode === 'chart' || viewMode === 'both') && renderCharts()}
        {(viewMode === 'table' || viewMode === 'both') && renderProfitabilityTable()}
      </>
    );
  };
  
  // Render payment schedules report
  const renderPaymentSchedules = () => {
    // Filter and sort payment schedules
    let schedules = reportData.schedules || [];
    if (searchTerm) {
      schedules = getFilteredData(schedules);
    }
    if (sortConfig.key) {
      schedules = getSortedData(schedules);
    }
    
    // Create data for payment status pie chart
    const statusCounts = {
      'Paid': 0,
      'Partial': 0,
      'Pending': 0,
      'Overdue': 0
    };
    
    schedules.forEach(schedule => {
      if (schedule.Status) {
        statusCounts[schedule.Status] = (statusCounts[schedule.Status] || 0) + 1;
      }
    });
    
    const statusChartData = Object.entries(statusCounts).map(([status, count], index) => ({
      name: status,
      value: count,
      color: COLORS[index % COLORS.length]
    }));
    
    // Create data for payment scheduled vs received by month
    const paymentsByMonth = reportData.paymentsByMonth || [];
    
    const renderCharts = () => (
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Payment Status Distribution</h5>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={statusChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                    nameKey="name"
                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {statusChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value} Schedules`, 'Count']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Scheduled vs. Received Payments</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={paymentsByMonth}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis 
                    tickFormatter={(value) => `$${Math.floor(value / 1000)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), '']}
                  />
                  <Legend />
                  <Bar dataKey="scheduled" name="Scheduled" fill="#8884d8" />
                  <Bar dataKey="received" name="Received" fill="#82ca9d" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
    
    const renderSchedulesTable = () => (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Payment Schedule Details</h5>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search schedules..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ width: '200px' }}
              />
              <Button 
                variant="outline-secondary" 
                size="sm"
                title="Export as CSV"
              >
                <FaFileDownload />
              </Button>
            </div>
          </div>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th onClick={() => requestSort('ProjectName')} className="sortable">
                  Project {getSortIcon('ProjectName')}
                </th>
                <th onClick={() => requestSort('ClientName')} className="sortable">
                  Client {getSortIcon('ClientName')}
                </th>
                <th onClick={() => requestSort('DueDate')} className="sortable">
                  Due Date {getSortIcon('DueDate')}
                </th>
                <th onClick={() => requestSort('PaymentDate')} className="sortable">
                  Payment Date {getSortIcon('PaymentDate')}
                </th>
                <th onClick={() => requestSort('TotalAmount')} className="sortable">
                  Total Amount {getSortIcon('TotalAmount')}
                </th>
                <th onClick={() => requestSort('AmountReceived')} className="sortable">
                  Amount Received {getSortIcon('AmountReceived')}
                </th>
                <th onClick={() => requestSort('Status')} className="sortable">
                  Status {getSortIcon('Status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {schedules.map((schedule, index) => (
                <tr key={index}>
                  <td>{schedule.ProjectName}</td>
                  <td>{schedule.ClientName}</td>
                  <td>{formatDate(schedule.DueDate)}</td>
                  <td>{schedule.PaymentDate ? formatDate(schedule.PaymentDate) : 'N/A'}</td>
                  <td>{formatCurrency(schedule.TotalAmount || 0)}</td>
                  <td>{formatCurrency(schedule.AmountReceived || 0)}</td>
                  <td>
                    <Badge bg={getStatusBadgeVariant(schedule.Status)}>
                      {schedule.Status}
                    </Badge>
                  </td>
                </tr>
              ))}
              {schedules.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center">No payment schedules found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
    
    return (
      <>
        <div className="summary-cards">
          <div className="summary-card">
            <h5>Total Scheduled</h5>
            <h3>{formatCurrency(summary.totalScheduled)}</h3>
          </div>
          <div className="summary-card">
            <h5>Total Received</h5>
            <h3>{formatCurrency(summary.totalReceived)}</h3>
          </div>
          <div className="summary-card">
            <h5>Completion Rate</h5>
            <h3>{formatPercent(summary.paymentCompletion)}</h3>
          </div>
          <div className="summary-card">
            <h5>Schedule Count</h5>
            <h3>{summary.scheduleCount}</h3>
          </div>
        </div>
        
        {(viewMode === 'chart' || viewMode === 'both') && renderCharts()}
        {(viewMode === 'table' || viewMode === 'both') && renderSchedulesTable()}
      </>
    );
  };
  
  // Render outstanding payments report
  const renderOutstandingPayments = () => {
    // Filter and sort payments
    let payments = reportData.payments || [];
    if (searchTerm) {
      payments = getFilteredData(payments);
    }
    if (sortConfig.key) {
      payments = getSortedData(payments);
    }
    
    // Create data for overdue by age chart
    const overdueByAge = reportData.overdueByAge || [];
    
    // Create data for outstanding by client chart
    const outstandingByClient = reportData.outstandingByClient || [];
    
    const renderCharts = () => (
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Overdue Amount by Age</h5>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={overdueByAge}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    fill="#8884d8"
                    paddingAngle={5}
                    dataKey="value"
                    nameKey="name"
                    label={({name, percent}) => `${name}: ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {overdueByAge.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [formatCurrency(value), 'Amount']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        
        <Col md={6}>
          <Card>
            <Card.Body>
              <h5 className="text-center mb-3">Outstanding Amount by Client</h5>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={outstandingByClient}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis 
                    tickFormatter={(value) => `$${Math.floor(value / 1000)}k`}
                  />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value), 'Outstanding']}
                  />
                  <Legend />
                  <Bar dataKey="value" name="Outstanding Amount" fill="#ff8042">
                    {outstandingByClient.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
    
    const renderPaymentsTable = () => (
      <Card>
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Outstanding Payment Details</h5>
            <div className="d-flex gap-2">
              <Form.Control
                type="text"
                placeholder="Search payments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                style={{ width: '200px' }}
              />
              <Button 
                variant="outline-secondary" 
                size="sm"
                title="Export as CSV"
              >
                <FaFileDownload />
              </Button>
            </div>
          </div>
          
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th onClick={() => requestSort('ProjectName')} className="sortable">
                  Project {getSortIcon('ProjectName')}
                </th>
                <th onClick={() => requestSort('ClientName')} className="sortable">
                  Client {getSortIcon('ClientName')}
                </th>
                <th onClick={() => requestSort('InvoiceNumber')} className="sortable">
                  Invoice # {getSortIcon('InvoiceNumber')}
                </th>
                <th onClick={() => requestSort('DueDate')} className="sortable">
                  Due Date {getSortIcon('DueDate')}
                </th>
                <th onClick={() => requestSort('AmountDue')} className="sortable">
                  Amount Due {getSortIcon('AmountDue')}
                </th>
                <th onClick={() => requestSort('DaysOutstanding')} className="sortable">
                  Days Outstanding {getSortIcon('DaysOutstanding')}
                </th>
                <th onClick={() => requestSort('Status')} className="sortable">
                  Status {getSortIcon('Status')}
                </th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment, index) => (
                <tr key={index}>
                  <td>{payment.ProjectName}</td>
                  <td>{payment.ClientName}</td>
                  <td>{payment.InvoiceNumber}</td>
                  <td>{formatDate(payment.DueDate)}</td>
                  <td>{formatCurrency(payment.AmountDue || 0)}</td>
                  <td className={payment.DaysOutstanding > 30 ? 'text-danger' : ''}>
                    {payment.DaysOutstanding}
                  </td>
                  <td>
                    <Badge bg={getStatusBadgeVariant(payment.Status)}>
                      {payment.Status}
                    </Badge>
                  </td>
                </tr>
              ))}
              {payments.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center">No outstanding payments found</td>
                </tr>
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>
    );
    
    return (
      <>
        <div className="summary-cards">
          <div className="summary-card">
            <h5>Total Outstanding</h5>
            <h3>{formatCurrency(summary.totalOutstanding)}</h3>
          </div>
          <div className="summary-card">
            <h5>Overdue Amount</h5>
            <h3 className="text-danger">{formatCurrency(summary.overdueAmount)}</h3>
          </div>
          <div className="summary-card">
            <h5>Overdue Percentage</h5>
            <h3 className="text-danger">{formatPercent(summary.overduePercentage)}</h3>
          </div>
          <div className="summary-card">
            <h5>Payment Count</h5>
            <h3>{summary.paymentCount}</h3>
          </div>
        </div>
        
        {(viewMode === 'chart' || viewMode === 'both') && renderCharts()}
        {(viewMode === 'table' || viewMode === 'both') && renderPaymentsTable()}
      </>
    );
  };
  
  // Render the appropriate report based on the report type
  switch (reportType) {
    case 'revenue-summary':
      return renderRevenueSummary();
    case 'project-profitability':
      return renderProjectProfitability();
    case 'payment-schedules':
      return renderPaymentSchedules();
    case 'outstanding-payments':
      return renderOutstandingPayments();
    default:
      return <div>Unknown report type</div>;
  }
};

export default FinancialReport;